import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import styles from './LastWreckScreen.module.css';
import { Icon } from './icons';
import type { LastWreckActions, ToolId, ToolState, Vector2 } from './types';

function VirtualStick({
  label,
  onChange,
  onRelease,
}: {
  label: string;
  onChange: (value: Vector2) => void;
  onRelease?: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Vector2>({ x: 0, y: 0 });

  const update = (event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    const radius = rect.width / 2;
    const rawX = (event.clientX - (rect.left + radius)) / radius;
    const rawY = (event.clientY - (rect.top + radius)) / radius;
    const magnitude = Math.hypot(rawX, rawY);
    const divisor = Math.max(1, magnitude);
    const next = { x: rawX / divisor, y: rawY / divisor };
    setPosition(next);
    onChange(next);
  };

  const release = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (rootRef.current?.hasPointerCapture(event.pointerId) === true) {
      rootRef.current.releasePointerCapture(event.pointerId);
    }
    setPosition({ x: 0, y: 0 });
    onChange({ x: 0, y: 0 });
    onRelease?.();
  };

  return (
    <div className={styles.stickWrap}>
      <span>{label}</span>
      <div
        aria-label={`${label} virtual stick`}
        className={styles.virtualStick}
        onPointerCancel={release}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          update(event);
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) update(event);
        }}
        onPointerUp={release}
        ref={rootRef}
        role="group"
      >
        <i
          style={{
            transform: `translate(${String(position.x * 28)}px, ${String(position.y * 28)}px)`,
          }}
        />
      </div>
    </div>
  );
}

const TOOL_META: Record<
  ToolId,
  { label: string; number: string; icon: 'scan' | 'cut' | 'grapple' }
> = {
  scanner: { label: 'Scan', number: '1', icon: 'scan' },
  cutter: { label: 'Cut', number: '2', icon: 'cut' },
  grapple: { label: 'Grapple', number: '3', icon: 'grapple' },
};

export function MobileControls({ tool, actions }: { tool: ToolState; actions: LastWreckActions }) {
  return (
    <div aria-label="Touch controls" className={styles.mobileControls}>
      <VirtualStick label="THRUST" onChange={actions.move} />
      <div className={styles.mobileToolRail}>
        {(Object.keys(TOOL_META) as ToolId[]).map((id) => (
          <button
            aria-label={`Select ${TOOL_META[id].label}`}
            aria-pressed={tool.selected === id}
            className={styles.mobileToolButton}
            disabled={tool.disabled === true}
            key={id}
            onClick={() => {
              actions.selectTool(id);
            }}
          >
            <Icon name={TOOL_META[id].icon} />
            <span>{TOOL_META[id].label}</span>
          </button>
        ))}
      </div>
      <VirtualStick
        label="AIM / USE"
        onChange={(vector) => {
          actions.aim(vector);
          actions.useTool(Math.hypot(vector.x, vector.y) > 0.3);
        }}
        onRelease={() => {
          actions.useTool(false);
        }}
      />
    </div>
  );
}

export { TOOL_META };
